import { EdgelessObject, type IFramework, type IResultOptions } from '@/framework';
import { createCanvas, loadImageAsync } from '@/utils/file-kit';
import type { IBackgroundLayerProps, ILayerProps } from '@/version-types';
import gridBgUrl from './assets/bg.png';
import { BackgroundImageSampler } from './render/draw-sampled-image';
import {
    buildImageModeDecorCanvas,
    compositeLayerCanvases,
    compositeWithGridBackground,
    renderBackgroundLayerCanvas,
} from './render/render-to-canvas';

/** 透明格子背景图，对应 app-background-wrap 的 CSS 背景 */
let gridBgImage: HTMLImageElement | null = null;
loadImageAsync(gridBgUrl).then((img) => {
    gridBgImage = img;
});

interface IGetResourceOptions {
    /** 是否绘制格子背景 */
    drawGridBackground?: boolean;
    /** 图片模式下，半透明图片渲染时在底部加白底色 */
    drawWhiteBackground?: boolean;
}

export class EdgelessBackgroundLayer extends EdgelessObject {
    /** 格子 + 白底等装饰层，不参与 opacity */
    private decorResource: HTMLCanvasElement | null = null;
    /** 图片层，单独施加 opacity */
    private imageResource: HTMLCanvasElement | null = null;
    /** 是否绘制额外的透明格子背景和图片模式白底 */
    private drawExtraBackground = true;
    private imageSampler?: BackgroundImageSampler;

    constructor(layerId: string, framework: IFramework) {
        super(layerId, framework.service);
    }

    private currentUrl: string | null = null;
    private currentImg: HTMLImageElement | null = null;
    /** 正在加载的 url 及其 Promise，同 url 并发加载时复用同一次请求 */
    private loadingUrl: string | null = null;
    private loadingPromise: Promise<void> | null = null;

    private getCurrentUrl() {
        const layer = this.service.flatLayer.getLayer(this.layerId);
        const data = layer.data as IBackgroundLayerProps;
        return data.url;
    }

    /**
     * 图片资源是否已与当前 url 对齐。
     * url 变化、或图片仍在加载中都视为未就绪，避免拿着未就绪的图片渲染出空白背景。
     */
    private checkResourceReady() {
        const url = this.getCurrentUrl();
        if (url !== this.currentUrl) return false;
        return url ? !!this.currentImg : !this.currentImg;
    }

    setDrawExtraBackground(enable: boolean) {
        if (this.drawExtraBackground === enable) return;
        this.drawExtraBackground = enable;
        this.update(false);
    }

    private getExtraBackgroundOptions(): IGetResourceOptions {
        return {
            drawGridBackground: this.drawExtraBackground,
            drawWhiteBackground: this.drawExtraBackground,
        };
    }

    private loadImage() {
        const url = this.getCurrentUrl();
        if (this.loadingPromise && this.loadingUrl === url) {
            return this.loadingPromise;
        }

        this.loadingUrl = url;
        const promise = (async () => {
            const img = url ? await loadImageAsync(url) : null;
            // 加载期间 url 可能又变了，只有仍是最新的一次才写入，避免旧图覆盖新图
            if (this.loadingUrl !== url) return;
            this.currentImg = img;
            this.currentUrl = url;
        })().finally(() => {
            if (this.loadingUrl === url) {
                this.loadingPromise = null;
            }
        });
        this.loadingPromise = promise;
        return promise;
    }

    private buildDecorResource(
        layer: ILayerProps,
        stageWidth: number,
        stageHeight: number,
        options: IGetResourceOptions,
    ) {
        let decorCanvas = buildImageModeDecorCanvas(layer, stageWidth, stageHeight, options.drawWhiteBackground);

        if (options.drawGridBackground && gridBgImage) {
            const gridBaseCanvas = decorCanvas ?? createCanvas(stageWidth, stageHeight).canvas;
            decorCanvas =
                compositeWithGridBackground(gridBaseCanvas, gridBgImage, stageWidth, stageHeight) ?? decorCanvas;
        }

        return decorCanvas;
    }

    private getResource(options: IGetResourceOptions = {}) {
        const layer = this.service.flatLayer.getLayer(this.layerId);
        const { width: stageWidth, height: stageHeight } = layer;
        const data = layer.data as IBackgroundLayerProps;
        const isImageMode = !!data.url;

        this.decorResource = null;
        this.imageResource = null;
        this.imageSampler?.clear();

        let baseImage: HTMLCanvasElement | HTMLImageElement | undefined;
        if (isImageMode) {
            try {
                baseImage = this.currentImg!;
            } catch (_) {
                baseImage = createCanvas(1, 1).canvas;
            }
        }

        if (isImageMode && (options.drawGridBackground || options.drawWhiteBackground)) {
            this.decorResource = this.buildDecorResource(layer, stageWidth, stageHeight, options);
            this.imageResource =
                renderBackgroundLayerCanvas({
                    layer,
                    stageWidth,
                    stageHeight,
                    baseImage,
                    drawWhiteBackground: false,
                    applyLayerOpacity: false,
                }) ?? null;

            if (!this.imageResource) {
                return createCanvas(stageWidth, stageHeight).canvas;
            }

            const canvas = compositeLayerCanvases(this.decorResource, this.imageResource, stageWidth, stageHeight);
            return canvas;
        }

        const contentCanvas = renderBackgroundLayerCanvas({
            layer,
            stageWidth,
            stageHeight,
            baseImage,
            drawWhiteBackground: isImageMode ? options.drawWhiteBackground : false,
        });

        if (!contentCanvas) {
            return createCanvas(stageWidth, stageHeight).canvas;
        }

        if (options.drawGridBackground && gridBgImage) {
            return compositeWithGridBackground(contentCanvas, gridBgImage, stageWidth, stageHeight) ?? contentCanvas;
        }

        return contentCanvas;
    }

    override render(ctx: CanvasRenderingContext2D) {
        if (this.noRender || this.domRender) return;

        const sampleImage = this.service.edgelessCanvas.isSingleMode;
        if (!sampleImage) {
            this.imageSampler?.clear();
            this.imageSampler = undefined;
        }
        const layer = this.service.flatLayer.getLayer(this.layerId);
        const dynamicInfo = (layer?.data as IBackgroundLayerProps | undefined)?.dynamicInfo;
        if (dynamicInfo && !dynamicInfo.hidden) {
            // 动态背景由 Frame 底部 DOM 层实时渲染。Canvas 保持透明，只保留图层水印，
            // 这样前景图层仍可在同一 Canvas 中正常覆盖动态背景。
            this.service.svipWatermark.drawLayerWatermark(this, ctx);
            return;
        }

        this.resetResource();

        if (this.decorResource || this.imageResource) {
            if (this.decorResource) {
                ctx.save();
                ctx.globalAlpha = 1;
                ctx.drawImage(this.decorResource, 0, 0, this.width, this.height);
                ctx.restore();
            }

            if (this.imageResource) {
                ctx.save();
                ctx.globalAlpha = this.opacity;
                if (sampleImage) {
                    this.imageSampler ??= new BackgroundImageSampler();
                    this.imageSampler.draw(ctx, this.imageResource, this.width, this.height);
                } else {
                    ctx.drawImage(this.imageResource, 0, 0, this.width, this.height);
                }
                ctx.restore();
            }
            this.service.svipWatermark.drawLayerWatermark(this, ctx);
            return;
        }

        // 纯色/渐变资源已包含图层透明度，不能再把外层 opacity 作用到透明棋盘格上。
        ctx.save();
        ctx.globalAlpha = 1;
        const resource = this.resource || this.service.edgelessCanvas.placeholderCanvas;
        if (
            sampleImage &&
            (layer?.data as IBackgroundLayerProps | undefined)?.url &&
            this.resource instanceof HTMLCanvasElement
        ) {
            this.imageSampler ??= new BackgroundImageSampler();
            this.imageSampler.draw(ctx, this.resource, this.width, this.height);
        } else {
            ctx.drawImage(resource, 0, 0, this.width, this.height);
        }
        ctx.restore();
        this.service.svipWatermark.drawLayerWatermark(this, ctx);
    }

    /**
     * 获取图层结果
     * @param aiWatermark - 是否绘制 ai 水印
     * @returns - 图层结果
     */
    override supportsCanvasResult() {
        return true;
    }

    override async getResult({ aiWatermark = false }: IResultOptions = {}) {
        const renderBox = this.getBaseInfo();
        const canvas = this.getResource();
        if (aiWatermark) {
            await super.drawAiWatermarkAsync(canvas);
        }
        return { canvas, renderBox };
    }

    override dispose() {
        this.imageSampler?.clear();
        super.dispose();
    }

    override loadResource() {
        const options = this.getExtraBackgroundOptions();
        if (!this.checkResourceReady()) {
            return this.loadImage().then(() => {
                const canvas = this.getResource(options);
                return super.drawAiWatermarkAsync(canvas).then(() => canvas);
            });
        }

        const canvas = this.getResource(options);
        super.drawAiWatermark(canvas);
        return canvas;
    }
}
